export const SHIPPED_BOT_AVATAR_VIEWBOX = "0 0 120 120";

/** One filled path per key; overlapping subpaths keep the same winding so they read as one body. */
export const SHIPPED_BOT_AVATAR_SHAPES: Record<string, string> = {
  round: "M17 61A43 43 0 1 0 103 61A43 43 0 1 0 17 61Z",
  cloud:
    "M18 65A21 21 0 1 0 60 65A21 21 0 1 0 18 65Z M33 49A27 27 0 1 0 87 49A27 27 0 1 0 33 49Z M64 65A20 20 0 1 0 104 65A20 20 0 1 0 64 65Z M31 72V72A14 14 0 0 0 45 86H85A14 14 0 0 0 99 72V72A14 14 0 0 0 85 58H45A14 14 0 0 0 31 72Z",
  gear: "M51.4756 20.8959L68.5244 20.8959L67.7646 32.0222L75 35.0192L82.3302 26.6145L94.3855 38.6698L85.9808 46L88.9778 53.2354L100.104 52.4756L100.104 69.5244L88.9778 68.7646L85.9808 76L94.3855 83.3302L82.3302 95.3855L75 86.9808L67.7646 89.9778L68.5244 101.104L51.4756 101.104L52.2354 89.9778L45 86.9808L37.6698 95.3855L25.6145 83.3302L34.0192 76L31.0222 68.7646L19.8959 69.5244L19.8959 52.4756L31.0222 53.2354L34.0192 46L25.6145 38.6698L37.6698 26.6145L45 35.0192L52.2354 32.0222Z",
  square:
    "M17 45V77A27 27 0 0 0 44 104H76A27 27 0 0 0 103 77V45A27 27 0 0 0 76 18H44A27 27 0 0 0 17 45Z",
  capsule:
    "M15 61V61A29 29 0 0 0 44 90H76A29 29 0 0 0 105 61V61A29 29 0 0 0 76 32H44A29 29 0 0 0 15 61Z",
  triangle: "M60 32L97 92L23 92Z",
  hexagon: "M60 26L94 45L94 77L60 96L26 77L26 45Z",
  drop: "M60 14 C74 37, 94 55, 94 74 A34 34 0 1 1 26 74 C26 55, 46 37, 60 14 Z",
  flower:
    "M42.5 38.5A17.5 17.5 0 1 0 77.5 38.5A17.5 17.5 0 1 0 42.5 38.5Z M62 49.75A17.5 17.5 0 1 0 97 49.75A17.5 17.5 0 1 0 62 49.75Z M62 72.25A17.5 17.5 0 1 0 97 72.25A17.5 17.5 0 1 0 62 72.25Z M42.5 83.5A17.5 17.5 0 1 0 77.5 83.5A17.5 17.5 0 1 0 42.5 83.5Z M23 72.25A17.5 17.5 0 1 0 58 72.25A17.5 17.5 0 1 0 23 72.25Z M23 49.75A17.5 17.5 0 1 0 58 49.75A17.5 17.5 0 1 0 23 49.75Z M43 61A17 17 0 1 0 77 61A17 17 0 1 0 43 61Z",
  star: "M60 23L69.4 48.1L96.1 49.3L75.2 65.9L82.3 91.7L60 77L37.7 91.7L44.8 65.9L23.9 49.3L50.6 48.1Z",
};

export const SHIPPED_BOT_AVATAR_SHAPE_KEYS = [
  "round",
  "cloud",
  "gear",
  "square",
  "capsule",
  "triangle",
  "hexagon",
  "drop",
  "flower",
  "star",
] as const;

/** Shapes with tapered corners carry their outline in the stroke instead of the point list. */
export const SHIPPED_BOT_AVATAR_STROKE_WIDTHS: Record<string, number> = {
  gear: 8,
  triangle: 15,
  hexagon: 14,
  star: 13,
};

export type ShippedBotAvatarFacePart = {
  /** A filled part, or a round-capped stroke when `strokeWidth` is set. */
  d: string;
  strokeWidth?: number;
  /** Degrees and the point the artwork rotated this part around. */
  rotate?: readonly [number, number, number];
};

/** Expressions drawn over the body in the contrast ink. */
export const SHIPPED_BOT_AVATAR_FACES: Record<string, readonly ShippedBotAvatarFacePart[]> = {
  round: [
    {
      d: "M36.5 51V69A7.5 7.5 0 0 0 44 76.5H44A7.5 7.5 0 0 0 51.5 69V51A7.5 7.5 0 0 0 44 43.5H44A7.5 7.5 0 0 0 36.5 51Z",
    },
    {
      d: "M68.5 51V69A7.5 7.5 0 0 0 76 76.5H76A7.5 7.5 0 0 0 83.5 69V51A7.5 7.5 0 0 0 76 43.5H76A7.5 7.5 0 0 0 68.5 51Z",
    },
  ],
  cloud: [
    { d: "M37.91 62.94 q7.05 -9.4 14.1 0", strokeWidth: 6.016 },
    { d: "M67.99 62.94 q7.05 -9.4 14.1 0", strokeWidth: 6.016 },
  ],
  gear: [
    {
      d: "M40.68 67.44A7.36 7.36 0 1 0 55.4 67.44A7.36 7.36 0 1 0 40.68 67.44Z",
    },
    {
      d: "M64.6 67.44A7.36 7.36 0 1 0 79.32 67.44A7.36 7.36 0 1 0 64.6 67.44Z",
    },
  ],
  square: [
    { d: "M34 59 L54 59", strokeWidth: 6.4 },
    { d: "M66 59 L86 59", strokeWidth: 6.4 },
  ],
  capsule: [
    {
      d: "M38.44 62.76V62.76A7.48 6.16 0 0 0 45.92 68.92H45.92A7.48 6.16 0 0 0 53.4 62.76V62.76A7.48 6.16 0 0 0 45.92 56.6H45.92A7.48 6.16 0 0 0 38.44 62.76Z",
      rotate: [-10, 45.92, 62.76],
    },
    {
      d: "M66.6 62.76V62.76A7.48 6.16 0 0 0 74.08 68.92H74.08A7.48 6.16 0 0 0 81.56 62.76V62.76A7.48 6.16 0 0 0 74.08 56.6H74.08A7.48 6.16 0 0 0 66.6 62.76Z",
      rotate: [10, 74.08, 62.76],
    },
  ],
  triangle: [
    { d: "M39 68.48 L54.12 68.48", strokeWidth: 5.376 },
    { d: "M65.88 74.36 L81 74.36", strokeWidth: 5.376 },
  ],
  hexagon: [
    {
      d: "M34.35 57.2A10.45 10.45 0 1 0 55.25 57.2A10.45 10.45 0 1 0 34.35 57.2Z",
    },
    {
      d: "M69.025 54.825V70.975A6.175 6.175 0 0 0 75.2 77.15H75.2A6.175 6.175 0 0 0 81.375 70.975V54.825A6.175 6.175 0 0 0 75.2 48.65H75.2A6.175 6.175 0 0 0 69.025 54.825Z",
      rotate: [-12, 75.2, 62.9],
    },
  ],
  drop: [
    { d: "M38.85 71.9 q6.75 9 13.5 0", strokeWidth: 5.76 },
    { d: "M67.65 71.9 q6.75 9 13.5 0", strokeWidth: 5.76 },
  ],
  flower: [
    {
      d: "M38.07 56.7A8.17 12.47 0 1 0 54.41 56.7A8.17 12.47 0 1 0 38.07 56.7Z",
    },
    {
      d: "M65.59 56.7A8.17 12.47 0 1 0 81.93 56.7A8.17 12.47 0 1 0 65.59 56.7Z",
    },
  ],
  star: [
    { d: "M41.38 63.52 q6.46 -11.4 12.92 0", strokeWidth: 4.864 },
    { d: "M65.7 63.52 q6.46 -11.4 12.92 0", strokeWidth: 4.864 },
  ],
};

const FALLBACK_SHAPE_KEY = "hexagon";

export type ShippedBotAvatarShape = {
  key: string;
  path: string;
  strokeWidth?: number;
  face: readonly ShippedBotAvatarFacePart[];
};

function normalizeShapeIndex(shapeIndex: number): number {
  const count = SHIPPED_BOT_AVATAR_SHAPE_KEYS.length;
  return Number.isFinite(shapeIndex) ? ((Math.trunc(shapeIndex) % count) + count) % count : 0;
}

/** Everything one shipped body needs: its path, outline width, and expression. */
export function shippedBotAvatarShape(shapeIndex: number): ShippedBotAvatarShape {
  const key = SHIPPED_BOT_AVATAR_SHAPE_KEYS[normalizeShapeIndex(shapeIndex)] ?? FALLBACK_SHAPE_KEY;
  return {
    key,
    path: SHIPPED_BOT_AVATAR_SHAPES[key] ?? SHIPPED_BOT_AVATAR_SHAPES[FALLBACK_SHAPE_KEY] ?? "",
    strokeWidth: SHIPPED_BOT_AVATAR_STROKE_WIDTHS[key],
    face: SHIPPED_BOT_AVATAR_FACES[key] ?? [],
  };
}

export function shippedBotAvatarShapePath(shapeIndex: number): string {
  return shippedBotAvatarShape(shapeIndex).path;
}
